import React, { useState, useRef, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Form, Button, Card, Space, Row, Col, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { CustomForm } from "ui/Form"
import { useWatch } from "antd/es/form/Form"
import { useSelector, useDispatch } from "react-redux"
import dayjs from "dayjs"
import ClientDetail from "./ClientDetail"
import ExperienceDetail from "./ExperienceDetail"
import PaymentMethod from "./PaymentMethod"
import PriceBreakdown from "./PriceBreakdown"
import PromoCodeInput from "ui/PromoCodeInput"
import Stepper from "./Stepper"
import styles from "./BookingForm.module.css"
import { Text, Title } from "ui/Typography"
import AddOnsSelector from "components/bookings/AddOnsSelector"
import { calculateAddOnsTotal } from "components/bookings/helpers"
import { calculateBookingTotal } from "../helpers/calculateBookingTotal"
import { resolveEffectiveDiscountPromos } from "utils/discountUtils"
import { updateBookingSelections } from "store/experiences"

const STEP1_BASE_FIELDS = ["experience", "date", "duration", "time"]
const STEP1_NEW_CLIENT_FIELDS = ["firstName", "lastName", "email", "phone_number"]
const STEP1_EXISTING_CLIENT_FIELDS = ["existing_client", "email"]

const BookingForm = ({ form, bookingUuid, bookAgain, booking, onSubmit, onCancelModal, filterExperienceIds, appliedPromos, onPromoApplied, onPromoRemoved, onClearPromos, onValuesChange }) => {
  const { t } = useTranslation()
  const [existingClientId, setExistingClientId] = useState(null)
  const [currentStep, setCurrentStep] = useState(1)
  const scrollContainerRef = useRef(null)
  const [summaryData, setSummaryData] = useState({})
  const step2FieldsRef = useRef(null)
  const mode = "guideBooking"
  const isExistingClient = useWatch("isExistingClient", form)
  const paymentType = useWatch("paymentType", form)
  const adjustedPrice = useWatch("adjustedPrice", form)
  const [alertMessage, setAlertMessage] = useState("")
  const isSearching = useSelector((state) => state.bookings.existing_clients_list.loading)
  const {
    experienceBookingData: { bookingSelections },
    experienceById: { experienceDetail }
  } = useSelector((state) => state.experiences)

  const dispatch = useDispatch()

  const isEdit = !!bookingUuid
  const handleAddOnsChange = useCallback(
    ({ target: { value: selections } }) => {
      const addOnsTotal = calculateAddOnsTotal(selections, experienceDetail?.add_ons)
      // Recompute the booking total here too — on step 2 the ExperienceDetail recalc
      // effect is unmounted, so changing add-ons would otherwise leave tax stale.
      const counts = bookingSelections?.counts
      const tripLength = bookingSelections?.tripLength
      const hasGuestCounts =
        counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
      const update = { addOns: selections, addOnsTotal }
      if (hasGuestCounts && tripLength) {
        const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
        const overrideGuestSubtotal = adjustedPrice != null && adjustedPrice > 0 ? adjustedPrice : undefined
        const { total, guestSubtotal, calculatedGuestSubtotal, breakdown } = calculateBookingTotal(
          { ...bookingSelections, addOnsTotal },
          counts,
          guideFeePercent,
          {
            paymentType,
            isEdit,
            originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
            customFees: experienceDetail?.assigned_custom_fees,
            platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
            discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit),
            overrideGuestSubtotal
          }
        ) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [] }
        update.amount = total
        update.baseAmount = guestSubtotal
        update.originalBaseAmount = calculatedGuestSubtotal
        update.breakdown = breakdown
      }
      dispatch(updateBookingSelections(update))
    },
    [dispatch, experienceDetail, booking, bookingSelections, paymentType, isEdit, appliedPromos, adjustedPrice]
  )

  // Tax & fees are computed on the discounted base, so applying/removing a promo must recompute
  // the total. ExperienceDetail (which recalcs on count/duration changes) is unmounted on step 2,
  // so handle promo changes here.
  useEffect(() => {
    const counts = bookingSelections?.counts
    const tripLength = bookingSelections?.tripLength
    const hasGuestCounts =
      counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
    if (!hasGuestCounts || !tripLength) return
    const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
    const overrideGuestSubtotal = adjustedPrice != null && adjustedPrice > 0 ? adjustedPrice : undefined
    const { total, guestSubtotal, calculatedGuestSubtotal, breakdown } = calculateBookingTotal(bookingSelections, counts, guideFeePercent, {
      paymentType,
      isEdit,
      originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
      customFees: experienceDetail?.assigned_custom_fees,
      platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
      discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit),
      overrideGuestSubtotal
    }) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [] }
    if (total !== bookingSelections?.amount) {
      dispatch(updateBookingSelections({ amount: total, baseAmount: guestSubtotal, originalBaseAmount: calculatedGuestSubtotal, breakdown }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedPromos])

  // Subtotal adjustment: recompute when the negotiated price changes. The override is read by
  // calculateBookingTotal via overrideGuestSubtotal; originalBaseAmount keeps the computed value
  // so PriceBreakdown can show the struck-through original.
  useEffect(() => {
    const counts = bookingSelections?.counts
    const tripLength = bookingSelections?.tripLength
    const hasGuestCounts =
      counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
    if (!hasGuestCounts || !tripLength) return
    const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
    const overrideGuestSubtotal = adjustedPrice != null && adjustedPrice > 0 ? adjustedPrice : undefined
    const { total, guestSubtotal, calculatedGuestSubtotal, breakdown } = calculateBookingTotal(bookingSelections, counts, guideFeePercent, {
      paymentType,
      isEdit,
      originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
      customFees: experienceDetail?.assigned_custom_fees,
      platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
      discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit),
      overrideGuestSubtotal
    }) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [] }
    dispatch(updateBookingSelections({ amount: total, baseAmount: guestSubtotal, originalBaseAmount: calculatedGuestSubtotal, breakdown }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustedPrice])

  const handleContinue = async () => {
    const clientFields = isExistingClient ? STEP1_EXISTING_CLIENT_FIELDS : STEP1_NEW_CLIENT_FIELDS
    const accessibilityRequired = form.getFieldValue("accessibility_required")
    const fieldsToValidate = [...clientFields, ...STEP1_BASE_FIELDS, ...(accessibilityRequired ? ["accessibility_notes"] : [])]
    try {
      await form.validateFields(fieldsToValidate)
      if (alertMessage) return
      setSummaryData(form.getFieldsValue(true))
      setCurrentStep(2)
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
    } catch {}
  }

  useEffect(() => {
    if (currentStep === 2 && step2FieldsRef.current) {
      form.setFieldsValue(step2FieldsRef.current)
    }
  }, [currentStep, form])

  const clientDisplayName = summaryData.isExistingClient
    ? summaryData.existing_client?.label
    : [summaryData.firstName, summaryData.lastName].filter(Boolean).join(" ")
  const summaryEmail = summaryData.email
  const experienceName = summaryData.experience?.label || experienceDetail?.title

  return (
    <Spin spinning={isSearching}>
      <CustomForm layout="vertical" form={form} onValuesChange={onValuesChange}>
        <Stepper activeStep={currentStep} t={t} />
        <div className={styles.scrollContainer} ref={scrollContainerRef}>
          {currentStep === 1 ? (
            <>
              <Card className={styles.cardContainer}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.client_detail")}
                </Title>
                <ClientDetail
                  setExistingClientId={setExistingClientId}
                  form={form}
                  bookingId={bookingUuid}
                  alertMessage={alertMessage}
                  setAlertMessage={setAlertMessage}
                  booking={booking}
                />
              </Card>
              <Card className={styles.cardContainer}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.experience_details")}
                </Title>
                <ExperienceDetail
                  form={form}
                  bookingId={bookingUuid}
                  booking={booking}
                  bookAgain={bookAgain}
                  mode={mode}
                  filterExperienceIds={filterExperienceIds}
                  appliedPromos={appliedPromos}
                />
              </Card>
            </>
          ) : (
            <>
              <Card className={styles.cardContainer}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.booking_summary", "Booking Summary")}
                </Title>
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={12}>
                    <Text fw={600} type="secondary">
                      {t("booking.labels.client_name", "Client Name")}:{" "}
                    </Text>
                    <Text>{clientDisplayName || "—"}</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text fw={600} type="secondary">
                      {t("booking.labels.client_email", "Client Email")}:{" "}
                    </Text>
                    <Text>{summaryEmail || "—"}</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text fw={600} type="secondary">
                      {t("booking.labels.experience")}:{" "}
                    </Text>
                    <Text>{experienceName || "—"}</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text fw={600} type="secondary">
                      {t("booking.labels.booked_date")}:{" "}
                    </Text>
                    {bookingSelections?.date ? (
                      <>
                        <Text>{dayjs(bookingSelections.date).format("MM/DD/YYYY")}</Text>
                        {bookingSelections?.timeSlot && <Text type="secondary"> · {bookingSelections.timeSlot}</Text>}
                      </>
                    ) : (
                      <Text>—</Text>
                    )}
                  </Col>
                </Row>
              </Card>
              <Card className={styles.cardContainer}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.payment_method")}
                </Title>
                <PaymentMethod
                  existingClientId={existingClientId}
                  setExistingClientId={setExistingClientId}
                  form={form}
                  bookingId={bookingUuid}
                  booking={booking}
                  bookAgain={bookAgain}
                  appliedPromos={appliedPromos}
                />
              </Card>
              <Card className={styles.cardContainer}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.price_breakdown")}
                </Title>
                {experienceDetail?.add_ons?.some((a) => a.enabled !== false) && (
                  <AddOnsSelector
                    addOns={experienceDetail.add_ons}
                    selections={bookingSelections?.addOns}
                    onChange={handleAddOnsChange}
                  />
                )}
                <PromoCodeInput
                  totalAmount={bookingSelections?.baseAmount ?? 0}
                  addOnsTotal={bookingSelections?.addOnsTotal ?? 0}
                  appliedPromos={appliedPromos}
                  onPromoApplied={onPromoApplied}
                  onPromoRemoved={onPromoRemoved}
                  experienceId={summaryData.experience?.key}
                  clientIdentifier={summaryData.email || summaryData.phone_number?.fullNumber}
                />
                <Form.Item name="adjustedPrice" hidden noStyle>
                  <input type="hidden" />
                </Form.Item>
                <Form.Item name="adjustmentReason" hidden noStyle>
                  <input type="hidden" />
                </Form.Item>
                <PriceBreakdown form={form} booking={booking} appliedPromos={appliedPromos} isEdit={isEdit} />
              </Card>
            </>
          )}
        </div>
        <Form.Item className={styles.formActions}>
          <Space>
            {currentStep === 1 ? (
              <>
                <Button variant="outlined" color="primary" htmlType="button" onClick={() => onCancelModal()}>
                  {t("global.cancel")}
                </Button>
                <Button type="primary" htmlType="button" onClick={handleContinue}>
                  {t("booking.continue_to_payment", "Continue to Payment")}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outlined" color="primary" htmlType="button" onClick={() => { onClearPromos?.(); setCurrentStep(1) }}>
                  {t("global.back", "Back")}
                </Button>
                <Button type="primary" htmlType="button" onClick={async () => {
                  try {
                    await form.validateFields(["paymentType"])
                    onSubmit()
                  } catch {}
                }}>
                  {bookingUuid && !bookAgain ? t("global.update") : t("global.submit")}
                </Button>
              </>
            )}
          </Space>
        </Form.Item>
      </CustomForm>
    </Spin>
  )
}

BookingForm.propTypes = {
  form: PropTypes.object.isRequired,
  bookingUuid: PropTypes.string.isRequired,
  bookAgain: PropTypes.object.isRequired,
  booking: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancelModal: PropTypes.func.isRequired,
  filterExperienceIds: PropTypes.arrayOf(PropTypes.number),
  appliedPromos: PropTypes.array.isRequired,
  onPromoApplied: PropTypes.func.isRequired,
  onPromoRemoved: PropTypes.func.isRequired,
  onClearPromos: PropTypes.func,
  onValuesChange: PropTypes.func
}

export default BookingForm
